import React, { memo, useEffect, useMemo } from 'react';
import { useContactContext } from '../../../context/ContactContext/context';
import { IPhonebookContact, IPhonebookContactDetail } from '../../../types';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import {
    selectBridgeRegexp,
    selectCanBlockNumbers,
    selectChatEnabled,
    selectCurrentMessageId,
    selectCurrentUserId,
    selectIsNebulaChat,
    selectIsNumberBlocking,
    selectPhonebookDictionary,
    selectUserCountryCode,
    startCall
} from '../../../redux/slices';
import { usePolyglot } from '../../../context/Polyglot';
import styles from '../syles.module.scss';
import { StyledButton } from '../../../components/StyledComponents';
import Icon from '../../../components/Icon';
import { getValidNumber } from '../../../components/sidebars/Contact/Contact';
import { usePostIntegrationMutation } from '../../../redux/services/chatApi';
import { useNumberBlockContext } from '../../../pages/phone/GetNumberBlockingContext';
import { BlockingNumber, numberBlockedText } from '../../../pages/phone/call-history/CallHistoryRow/CallHistoryRowDisplay';
import { selectDestinationConversationUuid, setActiveConversation } from '../../../redux/slices/chatNSlice';

interface DataItem {
    label: string;
    primary?: boolean;
    data: IPhonebookContactDetail[];
}


const BlockingController: React.FC<{ number: string, callNumber: () => void }> = ({ number, callNumber }) => {
    const numberBlocking = useTypedSelector(state => selectIsNumberBlocking(state, number));
    const canBlock = useTypedSelector(selectCanBlockNumbers)

    const { t } = usePolyglot();
    const { addNumberForCheck } = useNumberBlockContext();

    useEffect(() => {
        if (!numberBlocking) {
            addNumberForCheck(number)
        }
    }, [numberBlocking])

    return (
        <div className={styles.contact_number__callee}>
            <StyledButton
                buttonStyle='text'
                onClick={() =>
                    callNumber()
                }
                iconOnly
                disabled={numberBlocking?.blocking.outbound}
                title={numberBlocking?.blocking.outbound ?
                    'Outbound calls to this number are blocked' :
                    t('actions.start_call')
                }
            >
                <Icon
                    name='phoneCall'
                    ariaHidden
                    width={28}
                    height={28}
                />
            </StyledButton>
            {canBlock && numberBlocking ? (
                <BlockingNumber
                    inbound={numberBlocking.blocking.inbound ?? false}
                    outbound={numberBlocking.blocking.outbound ?? false}
                    number={number}
                />
            ) : null}
        </div>
    );
}

const DetailDisplay: React.FC<{ detail: IPhonebookContactDetail }> = ({ detail }) => {
    const contact = useContactContext().contact as IPhonebookContact;

    const countryCode = useTypedSelector(selectUserCountryCode);
    const bridgeRegexp = useTypedSelector(selectBridgeRegexp);


    const { t } = usePolyglot();

    const dispatch = useTypedDispatch();

    const validNumber = getValidNumber(detail?.value, countryCode, bridgeRegexp);

    const onPhonebookContactCall = (callNumber: string | undefined, displayName: string) => {
        if (!callNumber) return;

        dispatch(startCall(callNumber, displayName));
    };

    if (detail.type !== 1) {
        return (<div>
            <p>{detail.value}</p>
        </div>)
    }

    if (validNumber) {
        if (validNumber.meta === 'bridge') {
            let displayName: string;
            if (contact.first_name && contact.last_name) {
                displayName = `${contact.first_name} ${contact.last_name} (${detail.label})`;
            } else {
                displayName = `${contact.first_name ||
                    contact.last_name ||
                    contact.company_name
                    } (${detail.label})`;
            }

            return (
                <div>
                    <p>{detail.value}</p>
                    <StyledButton
                        buttonStyle='text'
                        title={t('actions.start_call')}
                        onClick={() =>
                            onPhonebookContactCall(
                                detail.value,
                                displayName
                            )
                        }
                        iconOnly
                    >
                        <Icon
                            name='phoneCall'
                            ariaHidden
                            width={28}
                            height={28}
                        />
                    </StyledButton>
                </div>
            )
        }
        return (
            <NumberDisplay
                detail={detail}
                number={validNumber.value}
            />
        );
    }

    return (
        <div>
            <p>{detail.value}</p>
        </div>
    )
}

const NumberDisplay: React.FC<{ detail: IPhonebookContactDetail, number: string }> = memo(({ detail, number }) => {
    const contact = useContactContext().contact as IPhonebookContact;
    const isNebulaChat = useTypedSelector(selectIsNebulaChat);

    const conversationUuid = useTypedSelector(state => selectDestinationConversationUuid(state, number));

    const { addNumberForCheck } = useNumberBlockContext();
    const { t } = usePolyglot();

    const numberBlocking = useTypedSelector(state => selectIsNumberBlocking(state, number));
    const selectedMessageId = useTypedSelector(selectCurrentMessageId);
    const chatEnabled = useTypedSelector(selectChatEnabled);
    const userId = useTypedSelector(selectCurrentUserId);

    const dispatch = useTypedDispatch();

    const [postIntegration] = usePostIntegrationMutation();

    const displayName = useMemo(() => {
        if (contact.first_name && contact.last_name) {
            return `${contact.first_name} ${contact.last_name} (${detail.label})`;
        }

        return `${contact.first_name || contact.last_name || contact.company_name} (${detail.label})`
    }, [])

    useEffect(() => {
        if (!numberBlocking) {
            addNumberForCheck(number)
        }
    }, [numberBlocking])

    return (
        <div>
            <p>{detail.value}<span>{numberBlockedText(numberBlocking?.blocking)}</span></p>
            {chatEnabled ? (
                <StyledButton
                    onClick={() => {
                        if (selectedMessageId) {
                            if (conversationUuid) {
                                dispatch(setActiveConversation(conversationUuid))
                            } else {
                                postIntegration({
                                    voip_user_uuid: userId,
                                    destination: number,
                                    channel: 1,
                                    name: displayName,
                                    is_nebula: isNebulaChat,
                                });
                            }
                        }
                    }
                    }
                    disabled={!selectedMessageId}
                    title={
                        selectedMessageId
                            ? t('actions.send_sms')
                            : t('phrases.sms_id_not_set')
                    }
                    buttonStyle='text'
                    iconOnly
                >
                    <Icon
                        name='SMS'
                        ariaHidden
                        width={28}
                        height={28}
                    />
                </StyledButton>
            ) : null}
            <BlockingController
                number={number}
                callNumber={() => dispatch(startCall(number, displayName))}
            />
        </div>
    );
});

export const PhonebookContactMain: React.FC = () => {
    const contact = useContactContext().contact as IPhonebookContact;

    const phonebookDictionary = useTypedSelector(selectPhonebookDictionary);

    const { t } = usePolyglot();

    const detailsMapped: DataItem[] = useMemo(() => {
        const arr: DataItem[] = [];
        const map: Record<string, IPhonebookContactDetail[]> = {};

        // eslint-disable-next-line no-underscore-dangle
        (contact.details || (contact as any)._details).forEach(detail => {
            if (!detail.label) {
                return;
            }
            if (detail.primary) {
                arr.push({
                    label: detail.label,
                    primary: true,
                    data: [detail]
                });
            } else {
                if (!map[detail.label]) {
                    map[detail.label] = [];
                }
                map[detail.label].push(detail);
            }
        });

        Object.entries(map).forEach(([key, value]) => {
            arr.push({
                label: key,
                data: value
            });
        });

        return arr;
    }, []);

    return (
        <>
            {contact.phone_book_uuid ? (
                <div className={styles.item}>
                    <label>{t('terms.phonebook', 1)}</label>
                    <div>
                        <p>{phonebookDictionary[contact.phone_book_uuid] || 'Unknown'}</p>
                    </div>
                </div>
            ) : null}
            {detailsMapped.map(detailSection => (
                <div className={styles.item} key={detailSection.label}>
                    <label>
                        {detailSection.label}
                        {detailSection.primary ? ` (${t('adjective.contact_primary')})` : ''}
                    </label>
                    <div className={styles.phonebook_item}>
                        {detailSection.data.map((detail, idx) => (
                            <DetailDisplay
                                detail={detail}
                                key={`${detail.value}_${idx}`}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </>
    );
};
